import { type FC } from "react";
import { useQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { BannerQuery } from "@/shared/graphql";
import { formatCurrency } from "@/partner/features/products";
import type { ProductFormPrice } from "../model";

function getLiveMinPrice(prices: ProductFormPrice[]): {
  value: number;
  oldValue: number | null;
  currency: ProductFormPrice["currency"];
} | null {
  const periods = prices.flatMap((price) =>
    price.periods
      .filter((period) => period.value !== "")
      .map((period) => ({
        value: Number(period.value) || 0,
        oldValue: period.oldValue ? Number(period.oldValue) : null,
        currency: price.currency,
      }))
  );

  if (periods.length === 0) return null;

  return periods.reduce((min, current) =>
    current.value < min.value ? current : min
  );
}

interface ProductPreviewCardProps {
  title: string;
  previewId: string | null;
  eventStartAt: string | null;
  eventFinishAt: string | null;
  prices: ProductFormPrice[];
}

/**
 * Живое превью карточки продукта (референс: Edit/ProductPreview/ProductPreview.js)
 * — без composition/донатов/видео-промо (см. отчёт, бэклог). URL обложки
 * получаем тем же BannerQuery, что и ProductPreviewImageField (по id).
 */
export const ProductPreviewCard: FC<ProductPreviewCardProps> = ({
  title,
  previewId,
  eventStartAt,
  eventFinishAt,
  prices,
}) => {
  const { t } = useTranslation();
  const minPrice = getLiveMinPrice(prices);
  const { data: previewData } = useQuery(BannerQuery, {
    variables: { id: previewId ?? "" },
    skip: !previewId,
  });
  const previewUrl = previewData?.file?.url;

  // Референс различает «одна дата» / «диапазон» / «один день» (см.
  // ProductPreview.js renderEventDate) — упрощаем до диапазон/одна дата, без
  // отдельного случая «в один день» (второстепенное визуальное отличие).
  const eventTime = eventStartAt
    ? eventFinishAt
      ? t("products.preview.eventDateRange", {
          eventStartAt,
          eventFinishAt,
        })
      : t("products.preview.eventDateSingle", { eventStartAt })
    : null;

  return (
    <Card elevation={0}>
      {previewUrl && (
        <CardMedia
          image={previewUrl}
          sx={{
            width: "100%",
            height: 0,
            paddingTop: "56%",
            backgroundColor: "#090909",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        />
      )}
      <CardContent>
        <Grid container spacing={2}>
          {title && (
            <Grid size={12}>
              <Typography variant="h5" color="text.primary">
                {title}
              </Typography>
            </Grid>
          )}
          {eventTime && (
            <Grid size={12}>
              <Chip label={eventTime} />
            </Grid>
          )}
        </Grid>
      </CardContent>
      {minPrice && (
        <Grid
          size={12}
          sx={{ display: "flex", justifyContent: "center", p: 2 }}
        >
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disableRipple
            fullWidth
            sx={{
              whiteSpace: "nowrap",
              color: "text.primary",
              backgroundColor: "transparent",
              border: "4px solid",
              borderColor: "primary.main",
              fontSize: "1.4rem",
              fontWeight: 600,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            {minPrice.oldValue && (
              <Typography
                component="span"
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  position: "relative",
                  display: "inline-block",
                  fontSize: "1.2rem",
                  mr: 1.25,
                  fontWeight: 600,
                  textDecoration: "line-through",
                }}
              >
                {formatCurrency(minPrice.oldValue, minPrice.currency)}
              </Typography>
            )}
            {formatCurrency(minPrice.value, minPrice.currency)}
          </Button>
        </Grid>
      )}
    </Card>
  );
};
